import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomDrawer from "./CustomDrawer/CustomDrawer";
import OptimizedImage from "./OptimizedImage";

function CartIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="w-[19px] h-[19px]"
    >
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.7 12.6a2 2 0 0 0 2 1.4h9.7a2 2 0 0 0 2-1.6L23 6H6" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="w-[18px] h-[18px]"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
      <line x1="15.4" y1="6.5" x2="8.6" y2="10.5" />
    </svg>
  );
}

/**
 * StoreTopBar
 *
 * Sticky sub-page header. Mirrors the home header's left cluster so any deep
 * page has a one-tap escape home: [hamburger] [logo→"/"] [title] … [cart?] [share?].
 * There is no back arrow — `onBack` is accepted for call-site compatibility and ignored.
 * Must sit inside a `relative` scroll container so `sticky top-0` works.
 *
 * @param {object}   props
 * @param {string}   props.title      - Page title, e.g. "About the store"
 * @param {function} [props.onBack]   - Ignored (kept so callers needn't change)
 * @param {function} [props.onCart]   - Cart handler; cart icon only renders when passed
 * @param {number}   [props.cartCount] - Badge on the cart icon; hidden when 0/omitted
 * @param {function} [props.onShare]  - Share handler; share icon only renders when passed
 * @param {string}   [props.logoUrl]  - Fallback logo when Redux storeDetails has none
 */
// eslint-disable-next-line no-unused-vars
export default function StoreTopBar({
  title,
  onBack,
  onCart,
  cartCount,
  onShare,
  logoUrl,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storeUrl = useSelector((s) => s.storeDetails)?.storeUrl;
  const logo = storeUrl || logoUrl;

  return (
    <div
      className="
        flex items-center gap-[11px]
        px-[15px] py-[13px]
        sticky top-0 z-20
        bg-wc-paper/[0.97]
        border-b border-wc-border
      "
      style={{ backdropFilter: "blur(2px)" }}
    >
      {/* Hamburger — same nav drawer as the home header */}
      <CustomDrawer />

      {/* Logo — one-tap escape to the storefront home */}
      {logo && (
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label={t("topBar.goHome")}
          className="w-9 h-9 flex-none rounded-full overflow-hidden bg-wc-gray grid place-items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-900"
        >
          <OptimizedImage
            src={logo}
            alt=""
            className="w-full h-full object-cover"
            width={72}
            height={72}
          />
        </button>
      )}

      <span className="flex-1 min-w-0 text-[15px] font-[500] text-ink-900 truncate">
        {title}
      </span>

      {onCart && (
        <button
          onClick={onCart}
          className="
            relative
            w-[36px] h-[36px] flex-none
            rounded-[10px] bg-wc-gray
            grid place-items-center
            text-ink-900
            active:bg-wc-border transition-colors
          "
          aria-label="Cart"
        >
          <CartIcon />
          {cartCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[9px] font-[600] min-w-[16px] h-[16px] rounded-full flex items-center justify-center leading-none px-[3px] pt-[1.8px]">
              {cartCount}
            </span>
          )}
        </button>
      )}

      {onShare && (
        <button
          onClick={onShare}
          className="
            w-[36px] h-[36px] flex-none
            rounded-[10px] bg-wc-gray
            grid place-items-center
            text-ink-700
            active:bg-wc-border transition-colors
          "
          aria-label="Share"
        >
          <ShareIcon />
        </button>
      )}
    </div>
  );
}
